import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  ColorPicker,
  Flex,
  Form,
  Image,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tabs,
  Tooltip,
  Typography,
  Upload,
  message,
  Table,
} from "antd";
import {
  DeleteOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import UnsavedChangesModal from "app/shared/ui/UnsavedChangesModal";
import ActiveStatusFormItem from "app/shared/components/Form/ActiveStatusFormItem";
import {
  entityNameMaxLengthRule,
  entityNameNoSpecialCharsRule,
} from "app/shared/validation/entityNameCharacters";
import RichTextEditor, { getRichTextPlainLength } from "app/shared/ui/RichTextEditor";
import { getReportTextReferenceData } from "app/shared/api/getReportTextReferenceData";
import {
  buildApiCentreBody,
  CountrySelect,
  CurrencySelect,
  DEFAULT_BRAND_COLOR,
  deriveIsoValue,
  accountNumberRules,
  achAbaRules,
  bankNameRules,
  beneficiaryNameRules,
  centreContactEmailRules,
  extractImageSource,
  handleFormValidationError,
  intermediarySwiftCodeRules,
  normalizeAddressForForm,
  normalizeHexColor,
  normalizeSwiftCode,
  optionalAddressCountryFormProps,
  phoneRule,
  pickFirstString,
  preventSpace,
  schoolSponsorshipNumberRules,
  swiftCodeRules,
  validateMaxSize,
  preserveUploadedImageValue,
} from "./centreFormShared";
import type { Centre } from "./actions";
import { fetchCentreMerged, saveCentre } from "./actions";
import { getCentreError, getCentreSaving } from "./selectors";
import type { ColumnsType } from "antd/es/table";
import {
  buildDivisionReportTextsPayload,
  buildEmptyReportTextFormState,
  buildReportTextTemplates,
  mapDivisionReportTextsToFormState,
  REPORT_TEXT_RECIPIENT_STUDENTS,
  type ReportTextTemplate,
  type ReportTextFormState,
} from "app/pages/DivisionManager/reportTextHelpers";

const { Title } = Typography;

const optionalPctFormRule = {
  validator(_: unknown, value: unknown) {
    if (value === null || value === undefined || value === "") {
      return Promise.resolve();
    }
    if (typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 100) {
      return Promise.resolve();
    }
    return Promise.reject(new Error("Enter a value between 0 and 100"));
  },
};

function parseCentreDetailsRoute(pathname: string): { isAdd: boolean; editId: string | null } {
  if (pathname.endsWith("/add")) {
    return { isAdd: true, editId: null };
  }
  const m = pathname.match(/\/centre-manager\/([^/]+)\/?$/);
  const seg = m?.[1];
  if (seg && seg !== "add") {
    return { isAdd: false, editId: seg };
  }
  return { isAdd: true, editId: null };
}

type CentreTabKey = "general" | "bank" | "bi" | "report-details";

const BANK_TAB_ROOTS = new Set([
  "vatNumber",
  "registrationNumber",
  "vatExemptionNumber",
  "chequePayableTo",
  "bankDetails",
  "bankAddress",
  "beneficiaryBankAddress",
  "intermediaryBankDetails",
]);

const BI_TAB_ROOTS = new Set(["guarantees", "individualsRatio", "staffingRatio", "emptyBeds"]);

function resolveCentreTabKey(fieldName: string | number | (string | number)[]): CentreTabKey {
  const root = String(Array.isArray(fieldName) ? fieldName[0] : fieldName);
  if (BANK_TAB_ROOTS.has(root)) return "bank";
  if (BI_TAB_ROOTS.has(root)) return "bi";
  if (root === "reportTextById") return "report-details";
  return "general";
}

const CentreDetailsPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdd, editId } = parseCentreDetailsRoute(location.pathname);

  const saving = useSelector(getCentreSaving);
  const error = useSelector(getCentreError);

  const [form] = Form.useForm<Centre>();
  const [activeTab, setActiveTab] = useState<CentreTabKey>("general");
  const [editing, setEditing] = useState<Centre | null>(null);
  const [detailLoading, setDetailLoading] = useState(!isAdd);
  const [initialFormValues, setInitialFormValues] = useState<any>(null);
  const [initialLogoCode, setInitialLogoCode] = useState<string | null | undefined>(undefined);
  const [reportTextTemplates, setReportTextTemplates] = useState<ReportTextTemplate[]>([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(null);
  const reportTextIdsRef = useRef<Record<string, number>>({});
  const reportTextValues = Form.useWatch("reportTextById", form) as ReportTextFormState | undefined;

  const logoCodeFromForm = Form.useWatch("logoCode", form);
  const [logoCode, setLogoCode] = useState<string | null | undefined>(logoCodeFromForm);
  const isUploadingRef = React.useRef(false);
  const directorSignatureCode = Form.useWatch(["centreDirector", "signatureCode"], form);
  const dosSignatureCode = Form.useWatch(["directorOfStudies", "signatureCode"], form);

  const logoPreview = useMemo(() => {
    if (!logoCode) return undefined;
    if (typeof logoCode === "string" && logoCode.trim().startsWith("data:")) {
      return logoCode.trim();
    }
    return extractImageSource(logoCode);
  }, [logoCode]);
  const directorSignaturePreview = useMemo(
    () => extractImageSource(directorSignatureCode),
    [directorSignatureCode]
  );
  const dosSignaturePreview = useMemo(
    () => extractImageSource(dosSignatureCode),
    [dosSignatureCode]
  );

  const [pendingAction, setPendingAction] = useState<
    "leave" | "remove-logo" | "remove-director-signature" | "remove-dos-signature" | null
  >(null);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  useEffect(() => {
    let mounted = true;
    getReportTextReferenceData("Centre")
      .then((items) => {
        if (!mounted) return;
        const templates = buildReportTextTemplates(items);
        setReportTextTemplates(templates);
        setSelectedDocumentId(templates[0]?.reportTextId ?? null);
      })
      .catch(() => {
        if (!mounted) return;
        setReportTextTemplates([]);
        setSelectedDocumentId(null);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    // Upload's Form.Item onChange emits a fileList/event object — ignore those so we
    // don't wipe the data URL set in customRequest (which would send logo: null to the API).
    if (isUploadingRef.current) return;
    if (
      logoCodeFromForm === null ||
      logoCodeFromForm === undefined ||
      typeof logoCodeFromForm === "string"
    ) {
      setLogoCode(logoCodeFromForm);
    }
  }, [logoCodeFromForm]);

  const normalizeFormValue = (value: any): any => {
    if (value === null || value === undefined) {
      return undefined;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed === "" ? undefined : trimmed;
    }
    if (typeof value === "boolean" || typeof value === "number") {
      return value;
    }
    if (Array.isArray(value)) {
      return value.length === 0 ? undefined : value.map(normalizeFormValue);
    }
    if (typeof value === "object") {
      const normalized: any = {};
      let hasValues = false;
      for (const key in value) {
        const normalizedVal = normalizeFormValue(value[key]);
        if (normalizedVal !== undefined) {
          normalized[key] = normalizedVal;
          hasValues = true;
        }
      }
      return hasValues ? normalized : undefined;
    }
    return value;
  };

  const normalizeFormValues = (values: any): any => {
    if (!values || typeof values !== "object") return {};
    const normalized: any = {};
    for (const key in values) {
      const normalizedVal = normalizeFormValue(values[key]);
      if (normalizedVal !== undefined) {
        normalized[key] = normalizedVal;
      }
    }
    return normalized;
  };

  const checkFormDirty = useCallback(() => {
    if (!initialFormValues) return false;

    const currentValues = form.getFieldsValue(true);
    const currentLogo = logoCode || currentValues.logoCode;

    const normalizedCurrentLogo = normalizeFormValue(currentLogo);
    const normalizedInitialLogo = normalizeFormValue(initialLogoCode);
    if (normalizedCurrentLogo !== normalizedInitialLogo) {
      return true;
    }

    const normalizedCurrent = normalizeFormValues(currentValues);
    const normalizedInitial = normalizeFormValues(initialFormValues);

    delete normalizedCurrent.logoCode;
    delete normalizedInitial.logoCode;

    const deepEqual = (a: any, b: any): boolean => {
      if (a === b) return true;
      if (a == null && b == null) return true;
      if (a == null || b == null) return false;
      if (typeof a !== "object" || typeof b !== "object") {
        return a === b;
      }

      if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false;
        return a.every((val, idx) => deepEqual(val, b[idx]));
      }

      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      if (keysA.length !== keysB.length) return false;

      for (const key of keysA) {
        if (!keysB.includes(key)) return false;
        if (!deepEqual(a[key], b[key])) return false;
      }
      return true;
    };

    return !deepEqual(normalizedCurrent, normalizedInitial);
  }, [form, initialFormValues, logoCode, initialLogoCode]);

  const handleBrandColorChange = useCallback(() => {
    form.validateFields(["brandColorHex"]).catch(() => {});
  }, [form]);

  const handleLogoRemove = useCallback(() => {
    setPendingAction("remove-logo");
  }, []);
  const handleDirectorSignatureRemove = useCallback(() => {
    setPendingAction("remove-director-signature");
  }, []);
  const handleDosSignatureRemove = useCallback(() => {
    setPendingAction("remove-dos-signature");
  }, []);

  const goBackToList = useCallback(() => {
    navigate("/centre-manager");
  }, [navigate]);

  const handleCancel = useCallback(() => {
    if (checkFormDirty()) {
      setPendingAction("leave");
    } else {
      goBackToList();
    }
  }, [checkFormDirty, goBackToList]);

  const handlePendingConfirm = useCallback(() => {
    setPendingAction((current) => {
      if (current === "remove-logo") {
        form.setFieldValue("logoCode", null);
        setLogoCode(null);
        message.success("Logo removed");
      } else if (current === "remove-director-signature") {
        form.setFieldValue(["centreDirector", "signatureCode"], null);
        message.success("Centre director signature removed");
      } else if (current === "remove-dos-signature") {
        form.setFieldValue(["directorOfStudies", "signatureCode"], null);
        message.success("Director of studies signature removed");
      } else if (current === "leave") {
        goBackToList();
      }
      return null;
    });
  }, [form, goBackToList]);

  const handlePendingStay = useCallback(() => {
    setPendingAction(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setActiveTab("general");
      if (isAdd) {
        setEditing(null);
        setDetailLoading(false);
        return;
      }
      if (!editId) {
        setDetailLoading(false);
        return;
      }
      setDetailLoading(true);
      const fromState = (location.state as { centre?: Centre } | null)?.centre;
      const seed: Centre = fromState ?? ({ id: editId } as Centre);
      const merged = await dispatch(fetchCentreMerged(seed) as any);
      if (!cancelled) {
        setEditing(merged);
        setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdd, editId, dispatch, location.state]);

  useEffect(() => {
    if (!isAdd && !editing) {
      return;
    }
    form.resetFields();
    if (isAdd) {
      const defaultValues = {
        active: true,
        printAs: "A4",
        physicalCentre: false,
        brandColorHex: DEFAULT_BRAND_COLOR,
        logoCode: undefined,
        centreDirector: { signatureCode: undefined },
        directorOfStudies: { signatureCode: undefined },
        reportTextById: buildEmptyReportTextFormState(reportTextTemplates),
      } as any;
      form.setFieldsValue(defaultValues);
      setLogoCode(undefined);
      reportTextIdsRef.current = {};
      setTimeout(() => {
        const currentValues = form.getFieldsValue(true);
        setInitialFormValues(JSON.parse(JSON.stringify(currentValues)));
        setInitialLogoCode(undefined);
      }, 0);
      return;
    }

    const existingLogo = extractImageSource(
      (editing as any)?.logo ?? (editing as any)?.logoCode ?? editing?.logoCode,
      pickFirstString(
        (editing as any)?.logo?.contentType,
        (editing as any)?.logoContentType,
        (editing as any)?.logoMimeType
      )
    );
    const directorSignatureExisting = extractImageSource(
      editing?.centreDirector?.signatureImage ??
        editing?.centreDirector?.signatureCode ??
        editing?.centreDirector,
      editing?.centreDirector?.signatureContentType
    );
    const dosSignatureExisting = extractImageSource(
      editing?.directorOfStudies?.signatureImage ??
        editing?.directorOfStudies?.signatureCode ??
        editing?.directorOfStudies,
      editing?.directorOfStudies?.signatureContentType
    );
    const countryIso = deriveIsoValue(
      (editing as any)?.country,
      (editing as any)?.countryCode,
      (editing as any)?.countryISOCode,
      editing?.centreAddress?.country,
      editing?.centreAddress?.countryISOCode
    );
    const centreAddressForm = normalizeAddressForForm(editing?.centreAddress);
    const bankAddressForm = normalizeAddressForForm(editing?.bankAddress);
    const beneficiaryBankAddressForm = normalizeAddressForForm(editing?.beneficiaryBankAddress);
    const intermediaryBankAddressForm = normalizeAddressForForm(
      editing?.intermediaryBankDetails?.address ?? (editing as any)?.intermediaryBankAddress
    );
    const intermediaryBankDetailsForm = editing?.intermediaryBankDetails
      ? {
          ...editing.intermediaryBankDetails,
          address: intermediaryBankAddressForm,
        }
      : undefined;

    const formValues = {
      ...editing,
      country: countryIso ?? undefined,
      brandColorHex: normalizeHexColor(editing?.brandColorHex) ?? DEFAULT_BRAND_COLOR,
      logoCode: existingLogo,
      centreAddress: centreAddressForm,
      bankAddress: bankAddressForm,
      beneficiaryBankAddress: beneficiaryBankAddressForm,
      intermediaryBankDetails: intermediaryBankDetailsForm,
      centreDirector: {
        ...(editing?.centreDirector ?? {}),
        signatureCode: directorSignatureExisting ?? undefined,
      },
      directorOfStudies: {
        ...(editing?.directorOfStudies ?? {}),
        signatureCode: dosSignatureExisting ?? undefined,
      },
    } as any;

    const centreReportTexts = (editing as any)?.reportTexts ?? [];
    const { reportTextById, existingIdsByReportTextRow } = mapDivisionReportTextsToFormState({
      templates: reportTextTemplates,
      divisionReportTexts: centreReportTexts,
    });
    reportTextIdsRef.current = existingIdsByReportTextRow;
    formValues.reportTextById = reportTextById;

    form.setFieldsValue(formValues);
    setLogoCode(existingLogo);
    setTimeout(() => {
      const currentValues = form.getFieldsValue(true);
      setInitialFormValues(JSON.parse(JSON.stringify(currentValues)));
      setInitialLogoCode(existingLogo);
    }, 0);
  }, [editing, form, isAdd, reportTextTemplates]);

  const prevSaving = useRef(saving);
  const prevEditingId = useRef(editing?.id);
  useEffect(() => {
    prevEditingId.current = editing?.id;
  }, [editing?.id]);

  useEffect(() => {
    if (prevSaving.current && !saving && !error) {
      const wasUpdate = !!prevEditingId.current;
      message.success(wasUpdate ? "Centre updated successfully" : "Centre created successfully");
      navigate("/centre-manager");
    }
    prevSaving.current = saving;
  }, [saving, error, navigate]);

  const pageTitle = isAdd ? "Add Centre" : "Update Centre";

  const onSave = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue(true);
      const logoCandidates = [logoCode, values.logoCode, form.getFieldValue("logoCode")];
      const logoValue = logoCandidates.find(
        (v) => typeof v === "string" && v.trim().length > 0
      );
      if (logoValue) {
        values.logoCode = logoValue;
      } else if (logoCandidates.some((v) => v === null)) {
        values.logoCode = null;
      }
      const apiBody = buildApiCentreBody(values, editing);
      const payload: any = { ...apiBody };
      if (editing?.id) {
        payload.centreId = editing.id;
      }

      const centreIdForReportTexts =
        typeof editing?.id === "number"
          ? editing.id
          : typeof editing?.id === "string"
            ? parseInt(String(editing.id).replace(/\D+/g, ""), 10)
            : null;

      const reportTextsPayload = buildDivisionReportTextsPayload({
        templates: reportTextTemplates,
        reportTextById: values.reportTextById as ReportTextFormState,
        existingIdsByReportTextRow: reportTextIdsRef.current,
        divisionId: null,
        centreId: Number.isFinite(centreIdForReportTexts as any) ? (centreIdForReportTexts as number) : null,
      });

      payload.reportTexts = reportTextsPayload.map((row: any) => {
        const { divisionId, centreId, isDeleted, ...rest } = row ?? {};
        const currentForWhomId = rest?.forWhomId ?? rest?.ForWhomId;
        const normalizedForWhomId =
          typeof currentForWhomId === "number" && Number.isFinite(currentForWhomId) && currentForWhomId > 0
            ? currentForWhomId
            : REPORT_TEXT_RECIPIENT_STUDENTS;
        return {
          ...rest,
          forWhomId: normalizedForWhomId,
        };
      });
      dispatch(saveCentre(payload) as any);
    } catch (e: any) {
      handleFormValidationError(e, form, {
        beforeFocus: (fieldName) => {
          setActiveTab(resolveCentreTabKey(fieldName));
        },
      });
    }
  };

  const generalTab = (
    <>
      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Form.Item
            label="Add Logo (image only)"
            name="logoCode"
            valuePropName="value"
            getValueFromEvent={(incoming) =>
              preserveUploadedImageValue(incoming, form.getFieldValue("logoCode"))
            }
          >
            <Upload.Dragger
              accept="image/*"
              maxCount={1}
              showUploadList={false}
              beforeUpload={validateMaxSize}
              customRequest={async (op) => {
                try {
                  const file = op.file as File;
                  const reader = new FileReader();
                  reader.onload = () => {
                    const dataUrl = String(reader.result);
                    if (dataUrl && dataUrl.startsWith("data:")) {
                      isUploadingRef.current = true;
                      setLogoCode(dataUrl);
                      form.setFieldValue("logoCode", dataUrl);
                      setTimeout(() => {
                        isUploadingRef.current = false;
                      }, 100);
                      op.onSuccess?.({}, new XMLHttpRequest());
                      message.success("Logo added");
                    } else {
                      op.onError?.(new Error("Failed to read file as data URL"));
                    }
                  };
                  reader.onerror = () => {
                    isUploadingRef.current = false;
                    op.onError?.(new Error("Failed to read file"));
                  };
                  reader.readAsDataURL(file);
                } catch {
                  isUploadingRef.current = false;
                  op.onError?.(new Error("Failed to read file"));
                }
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag image to upload</p>
              <p className="ant-upload-hint">PNG/JPG up to {import.meta.env.VITE_FILE_UPLOAD_MAX_SIZE_MB}MB</p>
            </Upload.Dragger>
            {logoPreview && (
              <Space orientation="vertical" size={8} style={{ marginTop: 12 }}>
                <div
                  style={{
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                    padding: 8,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <div style={{ marginBottom: 8, fontSize: 12, color: "#52c41a", fontWeight: 500 }}>
                    ✓ Logo uploaded successfully
                  </div>
                  <Image
                    src={logoPreview}
                    alt="Centre logo"
                    style={{
                      maxHeight: 120,
                      maxWidth: "100%",
                      objectFit: "contain",
                      display: "block",
                      margin: "0 auto",
                    }}
                    preview
                  />
                  {logoCode && typeof logoCode === "string" && logoCode.startsWith("data:") && (
                    <div style={{ marginTop: 8, fontSize: 11, color: "#666" }}>
                      {(() => {
                        const match = logoCode.match(/^data:([^;]+);base64,/);
                        const mimeType = match ? match[1] : "image";
                        const sizeKB = Math.round((logoCode.length * 3) / 4 / 1024);
                        return `${mimeType.split("/")[1]?.toUpperCase() || "IMAGE"} • ${sizeKB} KB`;
                      })()}
                    </div>
                  )}
                </div>
                <Button type="text" danger icon={<DeleteOutlined />} onClick={handleLogoRemove}>
                  Remove logo
                </Button>
              </Space>
            )}
            {!logoPreview && logoCode && (
              <div
                style={{
                  marginTop: 12,
                  padding: 8,
                  backgroundColor: "#fff7e6",
                  border: "1px solid #ffd591",
                  borderRadius: 4,
                }}
              >
                <div style={{ fontSize: 12, color: "#d46b08" }}>
                  ⚠ Logo data detected but preview unavailable
                </div>
              </div>
            )}
          </Form.Item>
        </Col>
        <Col xs={24} md={16}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Centre Name"
                name="centreName"
                rules={[
                  { required: true, message: "Centre Name is required" },
                  entityNameNoSpecialCharsRule,
                  entityNameMaxLengthRule,
                ]}
              >
                <Input maxLength={50} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Centre Code"
                name="centreCode"
                rules={[{ required: true, message: "Centre Code is required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="Currency"
                name="currency"
                rules={[{ required: true, message: "Currency is required" }]}
              >
                <CurrencySelect />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Print As" name="printAs">
                <Select
                  options={[
                    { value: "A4", label: "A4" },
                    { value: "Letter", label: "Letter" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="Brand Colour"
                name="brandColorHex"
                rules={[{ required: true, message: "Brand colour is required" }]}
                getValueFromEvent={(color: any) => {
                  const hexValue =
                    typeof color === "string" ? color : color?.toHexString?.() || color?.hex || "";
                  return normalizeHexColor(hexValue) ?? DEFAULT_BRAND_COLOR;
                }}
                normalize={(value) => normalizeHexColor(value) ?? DEFAULT_BRAND_COLOR}
              >
                <ColorPicker
                  format="hex"
                  onChange={handleBrandColorChange}
                  showText={(color) => {
                    const hex = typeof color === "string" ? color : color?.toHexString?.() || "";
                    return normalizeHexColor(hex) ?? DEFAULT_BRAND_COLOR;
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="General Email" name="generalEmail" rules={centreContactEmailRules}>
                <Input maxLength={50} onKeyDown={preventSpace} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Accommodation Email" name="accommodationEmail" rules={centreContactEmailRules}>
                <Input maxLength={50} onKeyDown={preventSpace} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Telephone No" name="telephoneNo" rules={[phoneRule]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Emergency No" name="emergencyNo" rules={[phoneRule]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Transfer Emergency No" name="transferEmergencyNo" rules={[phoneRule]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="School Sponsorship Number"
                name="schoolSponsorshipNumber"
                rules={schoolSponsorshipNumberRules}
              >
                <Input maxLength={10} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label="Physical Centre" name="physicalCentre" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} md={6} style={{ marginTop: "0" }}>
              <Form.Item shouldUpdate>
                {({ getFieldValue }) => {
                  const hasActiveSchools = getFieldValue("hasActiveSchools");
                  return (
                    <ActiveStatusFormItem
                      label={
                        <span>
                          Active&nbsp;
                          {hasActiveSchools && (
                            <Tooltip title="Cannot deactivate this centre because it has active schools associated with it.">
                              <InfoCircleOutlined />
                            </Tooltip>
                          )}
                        </span>
                      }
                      name="active"
                      disabled={hasActiveSchools}
                    />
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col xs={24} md={12}>
          <Card size="small" title="Director of Studies">
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name={["directorOfStudies", "name"]} label="Name">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={["directorOfStudies", "email"]}
                  label="Email"
                  rules={centreContactEmailRules}
                >
                  <Input maxLength={50} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Signature (image)"
                  name={["directorOfStudies", "signatureCode"]}
                  valuePropName="value"
                  getValueFromEvent={(incoming) =>
                    preserveUploadedImageValue(
                      incoming,
                      form.getFieldValue(["directorOfStudies", "signatureCode"])
                    )
                  }
                >
                  <Space orientation="vertical" size={12} style={{ width: "100%" }}>
                    <Upload
                      accept="image/*"
                      maxCount={1}
                      showUploadList={false}
                      beforeUpload={validateMaxSize}
                      customRequest={async (op) => {
                        const file = op.file as File;
                        const reader = new FileReader();
                        reader.onload = () => {
                          form.setFieldValue(["directorOfStudies", "signatureCode"], String(reader.result));
                          op.onSuccess?.({}, new XMLHttpRequest());
                          message.success("Signature added");
                        };
                        reader.onerror = () => op.onError?.(new Error("Failed to read file"));
                        reader.readAsDataURL(file);
                      }}
                    >
                      <Button>Upload Signature</Button>
                    </Upload>
                    {dosSignaturePreview && (
                      <div
                        style={{
                          border: "1px solid #d9d9d9",
                          borderRadius: 4,
                          padding: 12,
                          backgroundColor: "#fafafa",
                          textAlign: "center",
                        }}
                      >
                        <div style={{ marginBottom: 8, fontSize: 12, color: "#52c41a", fontWeight: 500 }}>
                          ✓ Signature uploaded
                        </div>
                        <Image
                          src={dosSignaturePreview}
                          alt="Director of studies signature"
                          style={{
                            maxHeight: 120,
                            maxWidth: "100%",
                            objectFit: "contain",
                            display: "block",
                            margin: "0 auto",
                          }}
                          preview
                        />
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={handleDosSignatureRemove}
                          style={{ marginTop: 8 }}
                        >
                          Remove signature
                        </Button>
                      </div>
                    )}
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card size="small" title="Centre Director">
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  name={["centreDirector", "name"]}
                  label="Name"
                  dependencies={[["centreDirector", "signatureCode"]]}
                  rules={[
                    {
                      validator(_, value) {
                        const signatureCode = form.getFieldValue(["centreDirector", "signatureCode"]);
                        const hasSignature = signatureCode != null && signatureCode !== "";
                        if (hasSignature && (!value || String(value).trim() === "")) {
                          return Promise.reject(
                            new Error("Centre director name is required when a signature is uploaded.")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={["centreDirector", "email"]}
                  label="Email"
                  rules={centreContactEmailRules}
                >
                  <Input maxLength={50} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Signature (image)"
                  name={["centreDirector", "signatureCode"]}
                  valuePropName="value"
                  getValueFromEvent={(incoming) =>
                    preserveUploadedImageValue(
                      incoming,
                      form.getFieldValue(["centreDirector", "signatureCode"])
                    )
                  }
                >
                  <Space orientation="vertical" size={12} style={{ width: "100%" }}>
                    <Upload
                      accept="image/*"
                      maxCount={1}
                      showUploadList={false}
                      beforeUpload={validateMaxSize}
                      customRequest={async (op) => {
                        const file = op.file as File;
                        const reader = new FileReader();
                        reader.onload = () => {
                          form.setFieldValue(["centreDirector", "signatureCode"], String(reader.result));
                          op.onSuccess?.({}, new XMLHttpRequest());
                          message.success("Signature added");
                        };
                        reader.onerror = () => op.onError?.(new Error("Failed to read file"));
                        reader.readAsDataURL(file);
                      }}
                    >
                      <Button>Upload Signature</Button>
                    </Upload>
                    {directorSignaturePreview && (
                      <div
                        style={{
                          border: "1px solid #d9d9d9",
                          borderRadius: 4,
                          padding: 12,
                          backgroundColor: "#fafafa",
                          textAlign: "center",
                        }}
                      >
                        <div style={{ marginBottom: 8, fontSize: 12, color: "#52c41a", fontWeight: 500 }}>
                          ✓ Signature uploaded
                        </div>
                        <Image
                          src={directorSignaturePreview}
                          alt="Centre director signature"
                          style={{
                            maxHeight: 120,
                            maxWidth: "100%",
                            objectFit: "contain",
                            display: "block",
                            margin: "0 auto",
                          }}
                          preview
                        />
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={handleDirectorSignatureRemove}
                          style={{ marginTop: 8 }}
                        >
                          Remove signature
                        </Button>
                      </div>
                    )}
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Card size="small" title="Centre Address" style={{ marginTop: 16 }}>
        <Row gutter={12}>
          <Col xs={24} md={6}>
            <Form.Item name={["centreAddress", "building"]} label="Building/Apartment" rules={[{ max: 150, message: "Building must be less or equal 150 characters" }]}>
              <Input maxLength={150} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name={["centreAddress", "street"]} label="Street" rules={[{ max: 100, message: "Street must be less or equal 100 characters" }]}>
              <Input maxLength={100} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name={["centreAddress", "postalCode"]} label="Postal Code" rules={[{ max: 50, message: "Postal Code must be less or equal 50 characters" }]}>
              <Input maxLength={50} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name={["centreAddress", "city"]} label="City" rules={[{ max: 100, message: "City must be less or equal 100 characters" }]}>
              <Input maxLength={100} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              name={["centreAddress", "country"]}
              label="Country"
              rules={[
                { required: true, message: "Address country is required" },
                { pattern: /^[A-Z]{2}$/, message: "Use ISO-2, e.g., GB" },
              ]}
            >
              <CountrySelect />
            </Form.Item>
          </Col>
        </Row>
      </Card>
    </>
  );

  const bankTab = (
    <>
      <Row gutter={12}>
        <Col xs={24} md={6}>
          <Form.Item name="vatNumber" label="VAT Number">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item name="registrationNumber" label="Registration Number">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item name="vatExemptionNumber" label="VAT Exemption Number">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item name="chequePayableTo" label="Cheque Payable To">
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Card size="small" title="Bank Details" style={{ marginTop: 8 }}>
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item
              name={["bankDetails", "beneficiaryName"]}
              label="Beneficiary Name"
              rules={[{ required: true }, ...beneficiaryNameRules]}
            >
              <Input maxLength={100} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name={["bankDetails", "accountNo"]}
              label="Account No."
              rules={[{ required: true }, ...accountNumberRules]}
            >
              <Input maxLength={50} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name={["bankDetails", "bankName"]}
              label="Bank Name"
              rules={[{ required: true }, ...bankNameRules]}
            >
              <Input maxLength={100} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name={["bankDetails", "iban"]} label="IBAN No">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name={["bankDetails", "swiftCode"]}
              label="Swift Code"
              rules={swiftCodeRules}
              normalize={normalizeSwiftCode}
              getValueFromEvent={(e) => normalizeSwiftCode(e?.target?.value)}
            >
              <Input maxLength={100} onKeyDown={preventSpace} style={{ textTransform: "uppercase" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name={["bankDetails", "branchCode"]} label="Branch Code">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name={["bankDetails", "abaRoutingNo"]}
              label="ABA Routing No"
              rules={[{ pattern: /^\d{9}$/, message: "ABA must be 9 digits" }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name={["bankDetails", "achAba"]}
              label="ACH ABA"
              rules={achAbaRules}
            >
              <Input maxLength={10} />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card size="small" title="Bank Address" style={{ marginTop: 16 }}>
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item name={["bankAddress", "building"]} label="Building/Apartment">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name={["bankAddress", "street"]} label="Street">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name={["bankAddress", "postalCode"]} label="Postal Code">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name={["bankAddress", "city"]} label="City">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name={["bankAddress", "country"]}
              label="Country"
              {...optionalAddressCountryFormProps(form, ["bankAddress"], editing?.bankAddress)}
            >
              <CountrySelect />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card size="small" title="Beneficiary Bank Address" style={{ marginTop: 16 }}>
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item name={["beneficiaryBankAddress", "building"]} label="Building/Apartment">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name={["beneficiaryBankAddress", "street"]} label="Street">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name={["beneficiaryBankAddress", "postalCode"]} label="Postal Code">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name={["beneficiaryBankAddress", "city"]} label="City">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name={["beneficiaryBankAddress", "country"]}
              label="Country"
              {...optionalAddressCountryFormProps(
                form,
                ["beneficiaryBankAddress"],
                editing?.beneficiaryBankAddress
              )}
            >
              <CountrySelect />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card size="small" title="Intermediary Bank Details & Address" style={{ marginTop: 16 }}>
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item
              name={["intermediaryBankDetails", "bankName"]}
              label="Bank Name"
              rules={bankNameRules}
            >
              <Input maxLength={100} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name={["intermediaryBankDetails", "swiftCode"]}
              label="Swift Code"
              rules={intermediarySwiftCodeRules}
              normalize={normalizeSwiftCode}
              getValueFromEvent={(e) => normalizeSwiftCode(e?.target?.value)}
            >
              <Input maxLength={100} onKeyDown={preventSpace} style={{ textTransform: "uppercase" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name={["intermediaryBankDetails", "address", "building"]} label="Building/Apartment">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name={["intermediaryBankDetails", "address", "street"]} label="Street">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name={["intermediaryBankDetails", "address", "postalCode"]} label="Postal Code">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name={["intermediaryBankDetails", "address", "city"]} label="City">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name={["intermediaryBankDetails", "address", "country"]}
              label="Country"
              {...optionalAddressCountryFormProps(
                form,
                ["intermediaryBankDetails", "address"],
                editing?.intermediaryBankDetails?.address
              )}
            >
              <CountrySelect />
            </Form.Item>
          </Col>
        </Row>
      </Card>
    </>
  );

  const biTab = (
    <>
      <Row gutter={16}>
        <Col xs={24} md={6}>
          <Form.Item label="Guarantees">
            <Space.Compact style={{ width: "100%" }}>
              <Form.Item name="guarantees" noStyle rules={[optionalPctFormRule]}>
                <InputNumber
                  min={0}
                  max={100}
                  step={1}
                  precision={0}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Space.Addon>%</Space.Addon>
            </Space.Compact>
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Individuals Ratio">
            <Space.Compact style={{ width: "100%" }}>
              <Form.Item name="individualsRatio" noStyle rules={[optionalPctFormRule]}>
                <InputNumber
                  min={0}
                  max={100}
                  step={1}
                  precision={0}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Space.Addon>%</Space.Addon>
            </Space.Compact>
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Staffing Ratio">
            <Space.Compact style={{ width: "100%" }}>
              <Form.Item name="staffingRatio" noStyle rules={[optionalPctFormRule]}>
                <InputNumber
                  min={0}
                  max={100}
                  step={1}
                  precision={0}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Space.Addon>%</Space.Addon>
            </Space.Compact>
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label="Empty Beds">
            <Space.Compact style={{ width: "100%" }}>
              <Form.Item name="emptyBeds" noStyle rules={[optionalPctFormRule]}>
                <InputNumber
                  min={0}
                  max={100}
                  step={1}
                  precision={0}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Space.Addon>%</Space.Addon>
            </Space.Compact>
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  const reportDetailsTab = (
    <Row gutter={16} style={{ minHeight: 400 }}>
      <Col xs={24} md={8}>
        <Table<ReportTextTemplate>
          columns={
            [
              {
                title: "Document",
                dataIndex: "name",
                key: "name",
                render: (text: string, record: ReportTextTemplate) => (
                  <a
                    onClick={() => setSelectedDocumentId(record.reportTextId)}
                    style={{
                      color: "#1890ff",
                      fontWeight: selectedDocumentId === record.reportTextId ? 500 : 400,
                    }}
                  >
                    {text}
                  </a>
                ),
              },
              { title: "Used In", dataIndex: "usedIn", key: "usedIn" },
            ] as ColumnsType<ReportTextTemplate>
          }
          dataSource={reportTextTemplates}
          rowKey="reportTextId"
          pagination={false}
          size="small"
          onRow={(record) => ({
            onClick: () => setSelectedDocumentId(record.reportTextId),
            style: {
              cursor: "pointer",
              backgroundColor: selectedDocumentId === record.reportTextId ? "#e6f7ff" : "transparent",
            },
          })}
        />
      </Col>
      <Col xs={24} md={16}>
        {(() => {
          const selected =
            reportTextTemplates.find((t) => t.reportTextId === selectedDocumentId) ??
            reportTextTemplates[0];
          if (!selected) {
            return (
              <div style={{ padding: "40px", textAlign: "center", color: "#999" }}>
                Select a document from the list to edit
              </div>
            );
          }

          const currentContent =
            reportTextValues?.[String(selected.reportTextId)]?.content ?? "";
          const charCount = getRichTextPlainLength(currentContent);

          return (
            <div style={{ padding: "0 16px" }}>
              <Title level={4} style={{ marginBottom: 8 }}>
                {selected.name}
              </Title>
              {selected.usedIn && (
                <div style={{ color: "#8c8c8c", marginBottom: 16 }}>{selected.usedIn}</div>
              )}
              <Form.Item
                name={["reportTextById", String(selected.reportTextId), "content"]}
                valuePropName="value"
                trigger="onChange"
                preserve
              >
                <RichTextEditor aria-label={selected.name} />
              </Form.Item>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: -16, marginBottom: 16 }}>
                <div />
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>Character count: {charCount}</div>
              </div>
            </div>
          );
        })()}
      </Col>
    </Row>
  );

  return (
    <>
      <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
        <Flex justify="space-between" align="flex-start" wrap gap={16}>
          <div>
            <Title level={3} style={{ marginTop: 0, marginBottom: 8 }}>
              {pageTitle}
            </Title>
            <Link to="/centre-manager" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <LeftOutlined />
              Back to centres
            </Link>
          </div>
          <Space>
            <Button onClick={handleCancel}>Cancel</Button>
            <Popconfirm
              title="Save Centre"
              description="Are you sure you want to save these details?"
              onConfirm={onSave}
              okButtonProps={{ loading: saving }}
            >
              <Button type="primary" icon={<SaveOutlined />} loading={saving}>
                Save
              </Button>
            </Popconfirm>
          </Space>
        </Flex>

        <Spin spinning={detailLoading} description="Loading centre...">
          <Form
            form={form}
            layout="vertical"
            disabled={saving}
            style={{ marginTop: 24 }}
            initialValues={{
              active: true,
              printAs: "A4",
              physicalCentre: false,
              brandColorHex: DEFAULT_BRAND_COLOR,
              hasActiveSchools: editing?.hasActiveSchools,
            }}
            validateMessages={{
              required: "${label} is required",
            }}
          >
            <Tabs
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key as CentreTabKey)}
              destroyOnHidden={false}
              items={[
                { key: "general", label: "General Information", forceRender: true, children: generalTab },
                { key: "bank", label: "Bank Details", forceRender: true, children: bankTab },
                { key: "bi", label: "BI Settings", forceRender: true, children: biTab },
                { key: "report-details", label: "Report Details", forceRender: true, children: reportDetailsTab },
              ]}
            />
          </Form>
        </Spin>
      </div>

      <UnsavedChangesModal
        open={pendingAction !== null}
        onLeave={handlePendingConfirm}
        onStay={handlePendingStay}
      />
    </>
  );
};

export default CentreDetailsPage;